import React, {useEffect, useState} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {InputText} from 'primereact/inputtext';
import {Button} from 'primereact/button';
import {classNames} from 'primereact/utils';
import FieldMessage from '../FieldMessage/FieldMessage';
import {OtpChallenge} from '../../services/authService';

const emailSchema = yup.object({email: yup.string().email().required()});
const codeSchema = yup.object({code: yup.string().matches(/^\d{6}$/, 'Informe os seis dígitos do código.').required()});
type Challenge = OtpChallenge & {email: string; expiresAt: number; resendAt: number};

type Props = {
  initialEmail?: string;
  requestCode: (email: string) => Promise<{data: OtpChallenge}>;
  verifyCode: (challengeId: string, code: string) => Promise<void>;
  onCancel: () => void;
  onError: (message: string) => void;
};

function maskEmail(email: string) {
  const [name, domain = ''] = email.split('@');
  const visible = name.slice(0, Math.min(2, name.length));
  return `${visible}${'*'.repeat(Math.max(2, name.length - visible.length))}@${domain}`;
}

export default function OtpLoginFlow({initialEmail = '', requestCode, verifyCode, onCancel, onError}: Props) {
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [challenge, setChallenge] = useState<Challenge | null>(null);
  const [loading, setLoading] = useState(false);
  const [now, setNow] = useState(Date.now());
  const emailForm = useForm({defaultValues: {email: initialEmail}, resolver: yupResolver(emailSchema)});
  const codeForm = useForm({defaultValues: {code: ''}, resolver: yupResolver(codeSchema)});

  useEffect(() => {
    if (step !== 'code') return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [step]);

  const expiresIn = challenge ? Math.max(0, Math.ceil((challenge.expiresAt - now) / 1000)) : 0;
  const resendIn = challenge ? Math.max(0, Math.ceil((challenge.resendAt - now) / 1000)) : 0;

  useEffect(() => {
    if (step === 'code' && challenge && expiresIn === 0) {
      emailForm.setValue('email', challenge.email);
      setChallenge(null); codeForm.reset(); setStep('email');
      onError('Código expirado. Solicite um novo código.');
    }
  }, [expiresIn, step, challenge, emailForm, codeForm, onError]);

  const send = async (email: string) => {
    try {
      setLoading(true);
      const {data} = await requestCode(email);
      const timestamp = Date.now();
      setNow(timestamp);
      setChallenge({...data, email, expiresAt: timestamp + data.expires_in * 1000, resendAt: timestamp + data.retry_after * 1000});
      codeForm.reset(); setStep('code');
    } catch (error: any) {
      onError(error.response?.data?.message || 'Não foi possível enviar o código. Tente novamente.');
      const retryAfter = Number(error.response?.data?.retry_after || 0);
      if (challenge && retryAfter) setChallenge({...challenge, resendAt: Date.now() + retryAfter * 1000});
    } finally { setLoading(false); }
  };

  const verify = async ({code}: {code: string}) => {
    if (!challenge || !expiresIn) return onError('Código inválido ou expirado. Solicite um novo código.');
    try {
      setLoading(true);
      await verifyCode(challenge.challenge_id, code);
      setChallenge(null); codeForm.reset();
    } catch { onError('Código inválido ou expirado. Solicite um novo código.'); codeForm.setFocus('code'); }
    finally { setLoading(false); }
  };

  if (step === 'email') return <form onSubmit={emailForm.handleSubmit(({email}) => send(email))}>
    <div className="mb-4">
      <h4 className="text-lg font-medium mt-0 mb-2" style={{color: '#293988'}}>Entrar com código por e-mail</h4>
      <p className="text-600 font-light line-height-3 mt-0 mb-0">Enviaremos um código de seis dígitos para seu e-mail.</p>
    </div>
    <label htmlFor="fellow-otp-email" className="block text-900 font-medium mb-2">E-mail:</label>
    <Controller name="email" control={emailForm.control} render={({field, fieldState}) =>
      <InputText {...field} id="fellow-otp-email" type="email" autoFocus autoComplete="email" placeholder="Insira seu e-mail"
                 className={classNames({'p-invalid': fieldState.invalid}, 'w-full')}/>}/>
    <FieldMessage name="email" error={emailForm.formState.errors.email}/>
    <Button type="submit" label="Enviar código" loading={loading} disabled={loading} icon="pi pi-envelope" className="w-full py-3 mt-4"/>
    <Button type="button" label="Voltar para entrar com senha" text className="w-full mt-2" onClick={onCancel}/>
  </form>;

  return challenge ? <form onSubmit={codeForm.handleSubmit(verify)}>
    <h4 className="text-xl mb-2">Digite o código</h4>
    <p role="status">{challenge.message}</p><p>Enviado para <b>{maskEmail(challenge.email)}</b>.</p>
    <label htmlFor="fellow-code" className="block text-900 font-medium mb-2">Código de acesso:</label>
    <Controller name="code" control={codeForm.control} render={({field, fieldState}) =>
      <InputText {...field} id="fellow-code" inputMode="numeric" autoComplete="one-time-code" autoFocus maxLength={6} placeholder="000000"
                 onChange={event => field.onChange(event.target.value.replace(/\D/g, '').slice(0, 6))}
                 className={classNames({'p-invalid': fieldState.invalid}, 'w-full text-center text-2xl')}/>}/>
    <FieldMessage name="code" error={codeForm.formState.errors.code}/>
    <small className="block mt-2" aria-live="polite">O código expira em {Math.floor(expiresIn / 60)}:{String(expiresIn % 60).padStart(2, '0')}.</small>
    <Button type="submit" label="Validar código" loading={loading} disabled={loading || !expiresIn} className="w-full py-3 mt-4"/>
    <Button type="button" label={resendIn ? `Reenviar código em ${resendIn}s` : 'Reenviar código'} outlined className="w-full py-3 mt-3"
            disabled={loading || resendIn > 0} onClick={() => send(challenge.email)}/>
    <Button type="button" label="Voltar e trocar o e-mail" text className="w-full mt-2" onClick={() => {
      emailForm.setValue('email', challenge.email); setChallenge(null); codeForm.reset(); setStep('email');
    }}/>
  </form> : null;
}
